/**
 * Admin Settlements Page
 *
 * Displays pending settlements for approval with verification data.
 * Allows admins to review and approve settlements before payment.
 * Supports both mint_revenue and registration_revenue settlement types.
 */

import React, { useEffect, useState } from 'react';
import {
  getAdminPendingSettlements,
  getAdminRecentSettlements,
  getAdminSettlementVerification,
  approveSettlement,
  markSettlementPaid,
  getFailedRegistrationPayments,
  type AdminSettlement,
  type SettlementVerificationData,
  type SettlementStatus,
  type FailedRegistrationPayment,
} from '../services/adminApi.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

/**
 * Format XRP amount
 */
function formatXrp(xrp: number | null | undefined): string {
  if (xrp === null || xrp === undefined) return '-';
  return `${xrp.toFixed(6)} XRP`;
}

/**
 * Format date to relative time or date string
 */
function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Check if settlement is a registration revenue type
 */
function isRegistrationRevenue(settlement: AdminSettlement | SettlementVerificationData): boolean {
  return settlement.settlement_type === 'registration_revenue';
}

/**
 * Status badge component
 */
function SettlementStatusBadge({ status }: { status: SettlementStatus }) {
  const config: Record<SettlementStatus, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
    approved: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Approved' },
    processing: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Processing' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
    failed_unverified: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Unverified' },
  };

  const { bg, text, label } = config[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bg} ${text}`}>
      {label}
    </span>
  );
}

/**
 * Settlement type badge
 */
function SettlementTypeBadge({ settlement }: { settlement: AdminSettlement }) {
  if (settlement.settlement_type === 'registration_revenue') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
        Registration
      </span>
    );
  }
  if (settlement.settlement_type === 'composition_fee') {
    return (
      <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700">
        Composition
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
      Mint
    </span>
  );
}

/**
 * Verification check icon
 */
function VerificationIcon({ passed }: { passed: boolean }) {
  if (passed) {
    return (
      <span className="inline-flex items-center text-green-600">
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center text-red-600">
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </span>
  );
}

type ViewMode = 'pending' | 'recent';

export function AdminSettlementsPage() {
  const [pendingSettlements, setPendingSettlements] = useState<AdminSettlement[]>([]);
  const [recentSettlements, setRecentSettlements] = useState<AdminSettlement[]>([]);
  const [failedPayments, setFailedPayments] = useState<FailedRegistrationPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('pending');

  // Detail view state
  const [selectedSettlement, setSelectedSettlement] = useState<SettlementVerificationData | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  // Post-approval status tracking
  const [trackingStatus, setTrackingStatus] = useState(false);

  // Mark as paid state
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidTxHash, setMarkPaidTxHash] = useState('');
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pendingResult, recentResult, failedResult] = await Promise.all([
        getAdminPendingSettlements({ limit: 100 }),
        getAdminRecentSettlements(),
        getFailedRegistrationPayments().catch(() => ({ payments: [], count: 0 })),
      ]);
      setPendingSettlements(pendingResult.settlements);
      setRecentSettlements(recentResult.settlements);
      setFailedPayments(failedResult.payments);
    } catch (err: any) {
      setError(err.message || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  };

  const loadSettlementDetail = async (id: string) => {
    try {
      setLoadingDetail(true);
      setApproveError(null);
      const data = await getAdminSettlementVerification(id);
      setSelectedSettlement(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load settlement details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedSettlement) return;

    try {
      setApproving(true);
      setApproveError(null);
      await approveSettlement(selectedSettlement.id);

      // Start polling for status updates
      setApproving(false);
      setTrackingStatus(true);

      const pollInterval = 3000;
      const maxPolls = 30; // ~90 seconds max
      let polls = 0;

      const poll = async () => {
        polls++;
        try {
          const updated = await getAdminSettlementVerification(selectedSettlement.id);
          setSelectedSettlement(updated);

          if (updated.status === 'completed' || updated.status === 'failed' || updated.status === 'failed_unverified') {
            setTrackingStatus(false);
            await loadData();
            return;
          }

          if (polls < maxPolls) {
            setTimeout(poll, pollInterval);
          } else {
            setTrackingStatus(false);
            await loadData();
          }
        } catch {
          setTrackingStatus(false);
          await loadData();
        }
      };

      setTimeout(poll, pollInterval);
    } catch (err: any) {
      setApproveError(err.message || 'Failed to approve settlement');
      setApproving(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedSettlement || !markPaidTxHash.trim()) return;

    try {
      setMarkingPaid(true);
      setMarkPaidError(null);
      await markSettlementPaid(selectedSettlement.id, markPaidTxHash.trim());
      setSelectedSettlement(null);
      setMarkPaidTxHash('');
      await loadData();
    } catch (err: any) {
      setMarkPaidError(err.message || 'Failed to mark settlement as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const settlements = viewMode === 'pending' ? pendingSettlements : recentSettlements;

  // Check if all verifications pass for approval button
  const canApprove = selectedSettlement?.status === 'pending' &&
    selectedSettlement.verification.payment_verified &&
    selectedSettlement.verification.mint_count_verified &&
    selectedSettlement.verification.all_mints_completed;

  const isRegRevenueModal = selectedSettlement ? isRegistrationRevenue(selectedSettlement) : false;

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Settlements</h1>
            <p className="mt-2 text-sm text-gray-600">
              Review and approve creator settlement payments
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Failed Registration Payments Banner */}
        {failedPayments.length > 0 && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  {failedPayments.length} Failed Registration Payment{failedPayments.length !== 1 ? 's' : ''} - May Need Refund
                </h3>
                <p className="mt-1 text-xs text-red-700">
                  These payments were confirmed on-chain but could not be applied (e.g., spots filled). Review and process refunds as needed.
                </p>
                <div className="mt-3 max-h-48 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left text-red-700">
                        <th className="pb-1 pr-4 font-medium">Launch</th>
                        <th className="pb-1 pr-4 font-medium">Buyer</th>
                        <th className="pb-1 pr-4 font-medium">Amount</th>
                        <th className="pb-1 pr-4 font-medium">Phase</th>
                        <th className="pb-1 pr-4 font-medium">Error</th>
                        <th className="pb-1 font-medium">TX Hash</th>
                      </tr>
                    </thead>
                    <tbody className="text-red-800">
                      {failedPayments.map((fp) => (
                        <tr key={fp.id} className="border-t border-red-100">
                          <td className="py-1.5 pr-4 font-medium">{fp.launch_name}</td>
                          <td className="py-1.5 pr-4 font-mono">
                            {fp.buyer_address.slice(0, 8)}...{fp.buyer_address.slice(-4)}
                          </td>
                          <td className="py-1.5 pr-4">{formatXrp(fp.payment_amount_xrp)}</td>
                          <td className="py-1.5 pr-4">{fp.phase}</td>
                          <td className="py-1.5 pr-4 max-w-[200px] truncate" title={fp.error_message || ''}>
                            {fp.error_message || '-'}
                          </td>
                          <td className="py-1.5">
                            <a
                              href={`https://bithomp.com/explorer/${fp.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-700 underline hover:text-red-900"
                            >
                              {fp.tx_hash.slice(0, 8)}...
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Summary */}
        <div className="mb-6 grid gap-4 sm:grid-cols-4">
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
            <div className="text-sm font-medium text-yellow-600">Pending Approval</div>
            <div className="mt-1 text-2xl font-bold text-yellow-700">
              {pendingSettlements.filter(s => s.status === 'pending').length}
            </div>
            {pendingSettlements.filter(s => s.status === 'pending').length > 0 && (
              <div className="mt-1 text-sm text-yellow-600">
                {formatXrp(pendingSettlements.filter(s => s.status === 'pending').reduce((sum, s) => sum + s.amount_xrp, 0))} total
              </div>
            )}
          </div>
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 shadow-sm">
            <div className="text-sm font-medium text-orange-600">Needs Review</div>
            <div className="mt-1 text-2xl font-bold text-orange-700">
              {pendingSettlements.filter(s => s.status === 'failed_unverified').length}
            </div>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
            <div className="text-sm font-medium text-blue-600">Processing</div>
            <div className="mt-1 text-2xl font-bold text-blue-700">
              {recentSettlements.filter(s => s.status === 'approved' || s.status === 'processing').length}
            </div>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
            <div className="text-sm font-medium text-green-600">Completed Today</div>
            <div className="mt-1 text-2xl font-bold text-green-700">
              {recentSettlements.filter(s => {
                if (s.status !== 'completed' || !s.completed_at) return false;
                const today = new Date();
                const completed = new Date(s.completed_at);
                return completed.toDateString() === today.toDateString();
              }).length}
            </div>
          </div>
        </div>

        {/* View Mode Tabs */}
        <div className="mb-6 flex gap-2 border-b border-gray-200 pb-4">
          <button
            onClick={() => setViewMode('pending')}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'pending'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Action Required
            <span className={`rounded-full px-2 py-0.5 text-xs ${
              viewMode === 'pending' ? 'bg-blue-200' : 'bg-gray-200'
            }`}>
              {pendingSettlements.length}
            </span>
          </button>
          <button
            onClick={() => setViewMode('recent')}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'recent'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Recent Activity
            <span className={`rounded-full px-2 py-0.5 text-xs ${
              viewMode === 'recent' ? 'bg-blue-200' : 'bg-gray-200'
            }`}>
              {recentSettlements.length}
            </span>
          </button>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadData} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && settlements.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading settlements...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && settlements.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">
              {viewMode === 'pending'
                ? 'No settlements requiring action.'
                : 'No recent settlements.'}
            </p>
          </div>
        )}

        {/* Settlements Table */}
        {settlements.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Collection
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Creator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {settlements.map((settlement) => (
                  <tr key={settlement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {settlement.launch_thumbnail_url && (
                          <img
                            src={settlement.launch_thumbnail_url}
                            alt=""
                            className="mr-3 h-10 w-10 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {settlement.launch_name || 'Unknown'}
                          </span>
                          {settlement.launch_slug && (
                            <div className="text-xs text-gray-500">/{settlement.launch_slug}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-500 font-mono">
                        {settlement.creator_address.slice(0, 8)}...{settlement.creator_address.slice(-4)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <SettlementTypeBadge settlement={settlement} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatXrp(settlement.amount_xrp)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {settlement.mint_count}{' '}
                      <span className="text-xs text-gray-400">
                        {settlement.settlement_type === 'registration_revenue' ? 'registrations' : settlement.settlement_type === 'composition_fee' ? 'builds' : 'mints'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <SettlementStatusBadge status={settlement.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(settlement.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => loadSettlementDetail(settlement.id)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {(selectedSettlement || loadingDetail) && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !approving && setSelectedSettlement(null)}
              />

              {/* Modal */}
              <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:align-middle">
                {loadingDetail ? (
                  <div className="p-12 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading settlement details...</p>
                  </div>
                ) : selectedSettlement && (
                  <>
                    {/* Modal Header */}
                    <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-medium text-gray-900">
                              Settlement Review
                            </h3>
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              isRegRevenueModal
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {isRegRevenueModal ? 'Registration Revenue' : 'Mint Revenue'}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            {selectedSettlement.launch_name}
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedSettlement(null)}
                          disabled={approving}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Modal Body */}
                    <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
                      {/* Verification Summary */}
                      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <h4 className="mb-3 text-sm font-medium text-gray-700">Verification Checks</h4>
                        <div className="grid gap-3 sm:grid-cols-3">
                          <div className="flex items-center gap-2">
                            <VerificationIcon passed={selectedSettlement.verification.payment_verified} />
                            <span className="text-sm text-gray-600">
                              Amount verified
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <VerificationIcon passed={selectedSettlement.verification.mint_count_verified} />
                            <span className="text-sm text-gray-600">
                              {isRegRevenueModal ? 'Payment count matches' : 'Mint count matches'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <VerificationIcon passed={selectedSettlement.verification.all_mints_completed} />
                            <span className="text-sm text-gray-600">
                              {isRegRevenueModal ? 'All payments confirmed' : 'All mints completed'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Settlement Details */}
                      <div className="mb-6 grid gap-4 sm:grid-cols-2">
                        <div className="rounded-lg border border-gray-200 p-4">
                          <h4 className="mb-3 text-sm font-medium text-gray-700">Settlement Amount</h4>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatXrp(selectedSettlement.amount_xrp)}
                          </div>
                          <div className="mt-1 text-sm text-gray-500">
                            {selectedSettlement.mint_count} {isRegRevenueModal ? 'registration payments' : 'mints'}
                          </div>
                        </div>
                        <div className="rounded-lg border border-gray-200 p-4">
                          <h4 className="mb-3 text-sm font-medium text-gray-700">Payments Received</h4>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatXrp(selectedSettlement.total_payments_received_xrp)}
                          </div>
                        </div>
                      </div>

                      {/* Launch Details */}
                      <div className="mb-6 rounded-lg border border-gray-200 p-4">
                        <h4 className="mb-3 text-sm font-medium text-gray-700">Launch Details</h4>
                        <dl className="grid gap-2 sm:grid-cols-2 text-sm">
                          <div>
                            <dt className="text-gray-500">Name</dt>
                            <dd className="font-medium text-gray-900">{selectedSettlement.launch_name}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Status</dt>
                            <dd className="font-medium text-gray-900">{selectedSettlement.launch_status}</dd>
                          </div>
                          <div>
                            <dt className="text-gray-500">Creator</dt>
                            <dd className="font-mono text-xs text-gray-900">{selectedSettlement.creator_address}</dd>
                          </div>
                          {!isRegRevenueModal && (
                            <>
                              <div>
                                <dt className="text-gray-500">Mint Price</dt>
                                <dd className="font-medium text-gray-900">
                                  {formatXrp(selectedSettlement.launch_mint_price_xrp)}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-gray-500">Minted / Supply</dt>
                                <dd className="font-medium text-gray-900">
                                  {selectedSettlement.launch_minted_count} / {selectedSettlement.launch_total_supply || '?'}
                                </dd>
                              </div>
                            </>
                          )}
                        </dl>
                      </div>

                      {/* Items Table - different for registration vs mint */}
                      {isRegRevenueModal ? (
                        /* Registration Payments Table */
                        <div className="rounded-lg border border-gray-200">
                          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                            <h4 className="text-sm font-medium text-gray-700">
                              Included Registration Payments ({selectedSettlement.mints.length})
                            </h4>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Buyer
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Phase
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Slots
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Amount
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Status
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Confirmed
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    TX Hash
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {selectedSettlement.mints.map((payment) => (
                                  <tr key={payment.id}>
                                    <td className="px-4 py-2 text-xs font-mono text-gray-600">
                                      {payment.buyer_address.slice(0, 8)}...{payment.buyer_address.slice(-4)}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                      {payment.phase || '-'}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                      {payment.quantity ?? '-'}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                      {formatXrp(Number(payment.payment_amount_drops) / 1_000_000)}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                        payment.status === 'confirmed'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {payment.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-500">
                                      {formatDate(payment.confirmed_at)}
                                    </td>
                                    <td className="px-4 py-2 text-xs font-mono text-gray-500">
                                      {payment.payment_tx_hash ? (
                                        <a
                                          href={`https://bithomp.com/explorer/${payment.payment_tx_hash}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800"
                                        >
                                          {payment.payment_tx_hash.slice(0, 8)}...
                                        </a>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        /* Mints Table (existing) */
                        <div className="rounded-lg border border-gray-200">
                          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                            <h4 className="text-sm font-medium text-gray-700">
                              Included Mints ({selectedSettlement.mints.length})
                            </h4>
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Buyer
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    NFT
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Amount
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Referral
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Status
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Minted
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Transferred
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    TX Hash
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {selectedSettlement.mints.map((mint) => (
                                  <tr key={mint.id}>
                                    <td className="px-4 py-2 text-xs font-mono text-gray-600">
                                      {mint.buyer_address.slice(0, 8)}...{mint.buyer_address.slice(-4)}
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                      {mint.nft_token_id ? (
                                        <a
                                          href={`/nfts/${mint.nft_token_id}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 font-medium"
                                        >
                                          {mint.item_name || mint.nft_token_id.slice(0, 8) + '...'}
                                        </a>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-xs text-gray-600">
                                      {formatXrp(Number(mint.payment_amount_drops) / 1_000_000)}
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                      {Number(mint.referral_fee_drops) > 0 ? (
                                        <span className="text-blue-600 font-medium">
                                          {formatXrp(Number(mint.referral_fee_drops) / 1_000_000)}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                        mint.status === 'completed'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {mint.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2">
                                      {mint.mint_tx_hash ? (
                                        <VerificationIcon passed={true} />
                                      ) : (
                                        <VerificationIcon passed={false} />
                                      )}
                                    </td>
                                    <td className="px-4 py-2">
                                      {mint.transfer_offer_id ? (
                                        <VerificationIcon passed={true} />
                                      ) : (
                                        <VerificationIcon passed={false} />
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-xs font-mono text-gray-500">
                                      {mint.payment_tx_hash ? (
                                        <a
                                          href={`https://bithomp.com/explorer/${mint.payment_tx_hash}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800"
                                        >
                                          {mint.payment_tx_hash.slice(0, 8)}...
                                        </a>
                                      ) : (
                                        <span className="text-gray-400">Free mint</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Error Messages */}
                      {approveError && (
                        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
                          {approveError}
                        </div>
                      )}

                      {/* Settlement Error Display */}
                      {selectedSettlement.error_message && (selectedSettlement.status === 'failed' || selectedSettlement.status === 'failed_unverified') && (
                        <div className={`mt-4 rounded-lg p-4 text-sm ${
                          selectedSettlement.status === 'failed_unverified'
                            ? 'border border-orange-200 bg-orange-50 text-orange-800'
                            : 'border border-red-200 bg-red-50 text-red-800'
                        }`}>
                          <div className="font-medium mb-1">
                            {selectedSettlement.status === 'failed_unverified'
                              ? 'Payment status unknown - manual verification required'
                              : 'Payment failed'}
                          </div>
                          <div className="text-xs opacity-80">{selectedSettlement.error_message}</div>
                          {selectedSettlement.tx_hash && (
                            <div className="mt-2 text-xs">
                              Possible TX:{' '}
                              <a
                                href={`https://bithomp.com/explorer/${selectedSettlement.tx_hash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline"
                              >
                                {selectedSettlement.tx_hash.slice(0, 12)}...
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Mark as Paid Form - for failed/failed_unverified settlements */}
                      {(selectedSettlement.status === 'failed' || selectedSettlement.status === 'failed_unverified') && (
                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Mark as Paid</h4>
                          <p className="text-xs text-gray-500 mb-3">
                            {selectedSettlement.status === 'failed_unverified'
                              ? 'Verify the transaction on-chain, then enter the TX hash to mark this settlement as completed.'
                              : 'If this payment was sent manually or through another channel, enter the TX hash to mark it as completed.'}
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={markPaidTxHash}
                              onChange={(e) => setMarkPaidTxHash(e.target.value)}
                              placeholder="Transaction hash (64 characters)"
                              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={markingPaid}
                            />
                            <button
                              onClick={handleMarkPaid}
                              disabled={markingPaid || !markPaidTxHash.trim()}
                              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {markingPaid ? 'Saving...' : 'Confirm Paid'}
                            </button>
                          </div>
                          {markPaidError && (
                            <div className="mt-2 text-xs text-red-600">{markPaidError}</div>
                          )}
                        </div>
                      )}

                      {/* Status Tracking Progress */}
                      {trackingStatus && (
                        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-r-transparent"></div>
                            <div>
                              <div className="text-sm font-medium text-blue-800">
                                {selectedSettlement.status === 'approved' && 'Settlement approved - waiting for payment...'}
                                {selectedSettlement.status === 'processing' && 'Payment being sent on-chain...'}
                              </div>
                              <div className="text-xs text-blue-600 mt-0.5">
                                This will update automatically when the payment completes.
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Completion Success */}
                      {selectedSettlement.status === 'completed' && selectedSettlement.tx_hash && (
                        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-green-800">
                            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Payment completed successfully
                          </div>
                          <div className="mt-1 text-xs text-green-700">
                            TX:{' '}
                            <a
                              href={`https://bithomp.com/explorer/${selectedSettlement.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              {selectedSettlement.tx_hash.slice(0, 16)}...
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Modal Footer */}
                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          Status: <SettlementStatusBadge status={selectedSettlement.status} />
                        </div>
                        <div className="flex gap-3">
                          <button
                            onClick={() => { setSelectedSettlement(null); setTrackingStatus(false); setMarkPaidTxHash(''); setMarkPaidError(null); }}
                            disabled={approving}
                            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            Close
                          </button>
                          {selectedSettlement.status === 'pending' && (
                            <button
                              onClick={handleApprove}
                              disabled={approving || !canApprove}
                              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              {approving ? 'Approving...' : 'Approve Settlement'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
